'use client';

import { useCallback } from 'react';
import { User } from '@velocity-exchange/sdk';
import {
	deleteUserIxs,
	getIdAndAuthorityFromKey,
} from '@velocity-exchange/common';
import invariant from 'tiny-invariant';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import { notify } from 'src/utils/notifications';
import { sleep } from 'src/utils/dateTime';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';

/**
 * Hook for deleting a user subaccount.
 * Handles the transaction, unsubscription, and account switching logic.
 */
export const useDeleteUser = () => {
	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const walletPubkey = useVelocityStore(
		(s) => s.wallet.current.adapter?.publicKey
	);
	const userStatsAccount = useVelocityStore((s) => s.userStatsAccount);
	const { captureEvent } = usePostHogCapture();
	const setVelocityAccountsStore = useVelocityAccountsStore((s) => s.set);
	const getTxParams = useGetTxParams();

	const deleteUserAction = useCallback(
		async (wallet: UiEnhancedIWalletV2, user: User) => {
			invariant(velocityClient, 'driftClient is required');
			invariant(userStatsAccount, 'userStatsAccount is required');

			const ixs = await deleteUserIxs({
				velocityClient: velocityClient,
				user,
				userStatsAccount,
			});
			await wallet.signAndSendTransaction(ixs, getTxParams());
		},
		[velocityClient, userStatsAccount, getTxParams]
	);

	const wrappedDeleteUser =
		useSponsoredSignVelocityActionWithFallback(deleteUserAction);

	const deleteUser = useCallback(
		async (user: User) => {
			const userAccount = user.getUserAccountOrThrow();
			const toastId = `delete-user-${userAccount.subAccountId}`;

			invariant(velocityClient, 'Failed to get drift client');
			invariant(walletPubkey, 'Failed to get wallet public key');
			invariant(
				walletPubkey.equals(userAccount.authority),
				'Wallet public key does not match user authority'
			);
			invariant(userStatsAccount, 'Failed to get user stats account');

			notify({
				type: 'awaiting_wallet_confirmation',
				id: toastId,
				showUntilCancelled: true,
			});

			try {
				// Need to get the account from accountState before deleting the user so we can unsubscribe after its deleted. Otherwise we can't unsubscribe from it because it was already removed and we will get an error
				const accountsState = useVelocityAccountsStore.getState();

				const userKey = velocityClient.getUserMapKey(
					userAccount.subAccountId,
					userAccount.authority
				);
				const account = accountsState.accounts[userKey ?? ''];
				const isDeletingCurrentAccount =
					accountsState.currentUserKey === userKey;

				await wrappedDeleteUser(user);

				// Cleanup: unsubscribe and remove from velocityClient users map
				try {
					await velocityClient.users.get(userKey)?.unsubscribe();
				} catch {
					// do nothing - best effort to unsubscribe
				}
				velocityClient.users.delete(userKey);

				// If we're deleting the currently selected account then switch accounts too
				if (isDeletingCurrentAccount) {
					const userKeys = Object.keys(accountsState.accounts).map(String);

					const userKeyToSwitchTo = userKeys.find(
						(accountUserKey) => accountUserKey !== userKey
					);

					const { userId, userAuthority } = getIdAndAuthorityFromKey(
						userKeyToSwitchTo ?? ''
					);

					if (userId !== undefined && userAuthority !== undefined) {
						await velocityClient.switchActiveUser(userId, userAuthority);
					}

					// If there are no other accounts then this will set the current account to undefined which is the correct thing to do
					setVelocityAccountsStore((s) => {
						s.currentUserKey = userKeyToSwitchTo;
					});
				}

				// Remove deleted user from store
				setVelocityAccountsStore((s) => {
					delete s.accounts[userKey];
				});

				try {
					await sleep(500);
					await account?.client?.unsubscribe();
				} catch {
					// Don't think we will ever get here but just doing something in case.
					account?.client?.eventEmitter.removeAllListeners();
				}

				captureEvent('onboarding_account_deleted');
				notify({
					type: 'success',
					message: 'Subaccount deleted.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
					lengthMs: 5000,
				});
			} catch (e) {
				console.error(e);
				notify({
					type: 'error',
					message: 'Subaccount could not be deleted.',
					id: toastId,
					updatePrevious: true,
					showUntilCancelled: false,
					lengthMs: 5000,
				});
			}
		},
		[
			velocityClient,
			walletPubkey,
			userStatsAccount,
			captureEvent,
			setVelocityAccountsStore,
			wrappedDeleteUser,
		]
	);

	return { deleteUser };
};
